'use client';

import { createContext, useContext, useLayoutEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'next/navigation';

export type QualityTier = 'pending' | 'high' | 'mid' | 'low';

export type QualityState = {
  tier: QualityTier;
  isMobileForced: boolean;
};

const QualityContext = createContext<QualityState | null>(null);
const STORAGE_KEY = 'wantek-tier-override';
// Tablets (768–1023, iPad portrait) get the mobile stack too: the desktop
// journey's camera framing clips exhibits and station copy at those aspect
// ratios, while the mobile grid+info stack is already tuned for them. The
// CSS side of this gate (.journey-page / .scroll-progress media queries in
// globals.css) must always use the same cut-off.
export const MOBILE_BREAKPOINT = 1024;

function isOverride(value: unknown): value is 'high' | 'mid' | 'low' {
  return value === 'high' || value === 'mid' || value === 'low';
}

function probeWebGL(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    if (!gl) return false;
    const ext = gl.getExtension('WEBGL_lose_context');
    ext?.loseContext();
    return true;
  } catch {
    return false;
  }
}

function detectTier(): 'high' | 'mid' | 'low' {
  if (typeof window === 'undefined') return 'low';

  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  if (reducedMotion) return 'low';

  if (window.innerWidth < MOBILE_BREAKPOINT) return 'low';

  if (!probeWebGL()) return 'low';

  const cores = navigator.hardwareConcurrency ?? 4;
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;

  if (cores >= 8 && memory >= 8) return 'high';
  if (cores >= 4 && memory >= 4) return 'mid';
  return 'low';
}

function resolveOverride(prop: 'high' | 'mid' | 'low' | undefined): 'high' | 'mid' | 'low' | undefined {
  if (prop) {
    try {
      localStorage.setItem(STORAGE_KEY, prop);
    } catch {
      // ignore quota/private-mode errors
    }
    return prop;
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isOverride(stored)) return stored;
  } catch {
    // ignore
  }
  return undefined;
}

export function QualityProvider({
  children,
  tierOverride,
}: {
  children: ReactNode;
  /**
   * Explicit override that wins over `?tier=` and stored values. Kept for
   * callers that already know the tier (e.g. test harness, embed contexts).
   * Page routes should NOT pass this — the provider reads `?tier=` directly
   * so the SAME provider sees the URL override across the whole tree (no
   * nested wrappers, no split-tier bugs between layout and inner pages).
   */
  tierOverride?: 'high' | 'mid' | 'low';
}) {
  const [state, setState] = useState<QualityState>({ tier: 'pending', isMobileForced: false });
  // Read the URL override at this provider level so MobileIntroGate, the
  // journey scaffold and the showcase all see the same tier. Before this
  // unification a second QualityProvider inside Journey was the only one
  // that received `?tier=` — the layout-level provider here ran a separate
  // detectTier(), so debug overrides reached the canvas but not the mobile
  // stack around it.
  const params = useSearchParams();
  const urlTier = params?.get('tier');
  const urlOverride = isOverride(urlTier) ? urlTier : undefined;
  const effectiveOverride = tierOverride ?? urlOverride;

  useLayoutEffect(() => {
    // matchMedia instead of a one-shot innerWidth read: a desktop window
    // resized to phone width must flip to the mobile stack (and back) live,
    // not only on the next full load. The listener fires exactly when the
    // breakpoint is crossed — no per-pixel resize churn, and the tier stays
    // stable for every resize that doesn't cross it.
    const mobileQuery = window.matchMedia(
      `(max-width: ${MOBILE_BREAKPOINT - 1}px)`,
    );

    const resolveTier = () => {
      const isMobileWidth = mobileQuery.matches;

      // Resolution order:
      //   1. URL override (`?tier=…` or explicit prop) — per-session debug or
      //      embed signal, honoured everywhere including mobile. Persists to
      //      localStorage so the next navigation keeps the same tier.
      //   2. Mobile-width default — ALWAYS 'low'. localStorage is intentionally
      //      ignored here. Without this, a stale override from a past desktop
      //      session ("I tried ?tier=high to debug something") sticks to mobile
      //      and forces the 3D journey to mount on a phone — which scroll-locks
      //      because Lenis is off on mobile and the journey hijack assumes it.
      //   3. Desktop with stored override — honour it (persisted user preference).
      //   4. Desktop fallback — detectTier().
      let tier: 'high' | 'mid' | 'low';
      let isMobileForced = false;

      if (effectiveOverride) {
        try {
          localStorage.setItem(STORAGE_KEY, effectiveOverride);
        } catch {
          // ignore quota/private-mode errors
        }
        tier = effectiveOverride;
        isMobileForced = effectiveOverride !== 'low' && isMobileWidth;
      } else if (isMobileWidth) {
        tier = 'low';
      } else {
        const stored = resolveOverride(undefined);
        tier = stored ?? detectTier();
      }
       
      setState({ tier, isMobileForced });
    };

    resolveTier();
    mobileQuery.addEventListener('change', resolveTier);
    return () => mobileQuery.removeEventListener('change', resolveTier);
  }, [effectiveOverride]);

  return <QualityContext.Provider value={state}>{children}</QualityContext.Provider>;
}

export function useQuality(): QualityState {
  const state = useContext(QualityContext);
  if (state === null) {
    throw new Error('useQuality must be used inside <QualityProvider>');
  }
  return state;
}
